import { FaroBetaBadge } from "@components/common/faro-beta-badge";
import { blue } from "@flat-ui";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Box, Link, Stack, Typography } from "@mui/material";

/** Optional tooltip text to show when the action switch is not checked */
export const TOOLTIP_TEXT_NOT_CHECKED = "Activate for project";

/** Optional tooltip text to show when the action switch is checked */
export const TOOLTIP_TEXT_CHECKED = "Deactivate for project";

interface Props {
  /** Name of the feature */
  featureName: string;

  /** Description of the feature */
  featureDescription?: string;

  /** URl for Learning more about the feature  */
  learnMoreUrl?: string;

  /** True if the divider should show  */
  shouldShowDivider?: boolean;

  /** True if the beta badge should show */
  shouldShowBetaBadge?: boolean;

  /** Whether a switch or a request button */
  actionComponent: JSX.Element;
}

/** Renders feature settings property description, learn more link, action switch */
export function FeatureSettingLayout({
  featureName,
  featureDescription,
  learnMoreUrl,
  shouldShowBetaBadge,
  actionComponent,
}: Props): JSX.Element | null {
  const { isScreenMdOrSmaller, isScreenLg, isScreenXl, isScreenXs } =
    useMediaQueryList();
  const shouldStackTextContent =
    isScreenMdOrSmaller || isScreenLg || isScreenXl;
  const shouldWrapDescriptionLine = isScreenXs;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
      }}
    >
      <Stack
        direction={shouldStackTextContent ? "column" : "row"}
        sx={{
          alignItems: shouldStackTextContent ? "flex-start" : "center",
          gap: 1,
          flex: 1,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: shouldStackTextContent ? "100%" : "auto",
            minWidth: 0,
          }}
        >
          <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
            {featureName}
          </Typography>

          {/* Show a beta badge if the feature is in beta stage */}
          {shouldShowBetaBadge && <FaroBetaBadge />}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: shouldWrapDescriptionLine ? "wrap" : "nowrap",
            width: shouldStackTextContent ? "100%" : "auto",
            minWidth: 0,
          }}
        >
          {featureDescription && (
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: "normal",
              }}
            >
              {!shouldStackTextContent && " - "}
              {featureDescription}
            </Typography>
          )}
          {learnMoreUrl && (
            <Link
              href={learnMoreUrl}
              target="_blank"
              color={blue[500]}
              underline="always"
              sx={{
                fontSize: "14px",
                fontWeight: "600",
                textDecorationColor: blue[500],
                whiteSpace: "nowrap",
              }}
            >
              Learn more
            </Link>
          )}
        </Box>
      </Stack>
      <Box sx={{ display: "flex", width: "50px" }}>{actionComponent}</Box>
    </Box>
  );
}
